import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import {
  AppDatePicker,
  AppFileUploader,
  AppInputNumber,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
  AppTextArea,
} from '@/layout/AppComponents';
import { toFormData } from '../../../minions/forms';
import ModuleComsaSideBarReports from './ModuleComsaSideBarReports';
import Translator from '../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath } from '../../../routing/helpers/router';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { handleMinDate } from '@/layout/screenSpecifics/Modules/ComsaReports/tools/comsaReportsTools';

const ModuleComsaNaturalResourcesUseReductionReportScreen = () => {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState({});
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [reportFormInitialValues, setReportFormInitialValues] = useState({
    id: null,
    updateDate: null,
    reuseResourcesTotal: 0,
    reuseResourcesDirect: 0,
    reuseResourcesCrushing: 0,
    excavationVolumeProject: 0,
    excavationVolumeReal: 0,
    reductionLandDischargeLandfillProject: 0,
    reductionLandDischargeLandfillReal: 0,
    landUseFromPlantsProject: 0,
    landUseFromPlantsEvidence: null,
    deleteLandUseFromPlantsEvidence: 0,
    landUseInPlantsProject: 0,
    landUseInPlantsEvidence: null,
    deleteLandUseInPlantsEvidence: 0,
    loansUseProject: 0,
    loansUseReal: 0,
    comment: '',
    waterVolumeWasteWaterTreatedReal: 0,
    waterEstimatedWaterUsage: 0,
    waterActualUseRecycledWaterReal: 0,
    waterUse: 0,
    wasteTotalConstruction: 0,
    wasteAttorneyGeneralOffice: 0,
    landsReuseSoilConstructionReal: 0,
    landsReuseSoilOutsideConstructionReal: 0,
    landsEstimatedLandReuseProject: 0,
    stoneRecyclingStoneMaterialsOffsite: 0,
    stoneRecyclingStoneMaterialsOnSite: 0,
    stoneEstimatedRockMaterialProject: 0,
    consumptionRecycledMaterialsReal: 0,
    consumptionUseRecycledMaterialsProject: 0,
    consumptionSoilOtherConstruction: 0,
    consumptionFromOtherConstruction: 0,
  });
  const [reportType] = useState('naturalResourcesUseReduction');
  const [moduleData, setModuleData] = useState(null);

  // > LOAD REPORT //
  const loadReport = useCallback(async () => {
    await axios
      .post(axiosPath('api_r_user_module_comsaReports_report'), {
        type: reportType,
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current && r.data.id) {
          setReportFormInitialValues(r.data);
          setReport(r.data);
        }
      })
      .finally(() => {
        if (isMounted.current) setLoading(false);
      });
  }, [reportType, moduleBridgeHex, subModuleBridgeHex, plantHex]);

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    await axios
      .post(axiosPath('api_r_user_module_comsaReports_getModuleData'), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then(({ data }) => {
        if (isMounted.current) setModuleData(data);
      })
      .catch((err) => {
        dispatch(errorHandler(err));
      });
  }, [moduleBridgeHex, subModuleBridgeHex, plantHex]);

  useEffect(() => {
    loadModuleData();
  }, [loadModuleData]);
  // > LOAD MODULE DATA //

  useEffect(() => {
    if (loading) {
      loadReport();
    }
  }, [loading, loadReport]);
  // < LOAD REPORT //

  // < EDIT REPORT
  const editReport = async (values) => {
    setIsSendingReport(true);
    await axios
      .post(
        axiosPath('api_r_user_module_comsaReports_editReport'),
        toFormData({
          ...values,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        }),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => {
        if (isMounted.current) {
          AppNotificationAlert({
            title: r.data.notification.title,
            message: r.data.notification.message,
            type: 'success',
            position: 'top-right',
            duration: 10000,
          });
          setReportFormInitialValues(r.data.naturalResourcesUseReduction);
          setReport(r.data.naturalResourcesUseReduction);
        }
      })
      .finally(() => {
        if (isMounted.current) setIsSendingReport(false);
      });
  };
  // < EDIT REPORT

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    reuseResourcesDirect: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
    reuseResourcesCrushing: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
    excavationVolumeProject: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
    excavationVolumeReal: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
    reductionLandDischargeLandfillProject: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
    reductionLandDischargeLandfillReal: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
    landUseFromPlantsProject: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
    landUseInPlantsProject: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
    loansUseProject: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
    loansUseReal: Yup.string().matches(
      /^\d+(\.\d{1,3})?$/,
      ReactDOMServer.renderToString(
        <Translator file="module-comsaReports" name="0.000.format" />,
      ),
    ),
  });
  // < FORM SCHEMA

  const getFile = (url, typeID, salt) =>
    axios
      .post(axiosPath(url), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
        typeID,
        salt,
      })
      .then(({ data }) => (window.location = data))
      .catch((e) => dispatch(errorHandler(e)));

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${moduleData.plantName} - ${ReactDOMServer.renderToString(
                    <Translator file="module-legalCompliance" name="evaluation" />,
                  )}`,
                  route: {
                    name: 'user_module_legalCompliance_evaluation',
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator file="module-comsaReports" name="indicators" />,
                  ),
                  route: {
                    name: 'user_module_comsaReports_home',
                    params: { moduleBridgeHex, subModuleBridgeHex, plantHex },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaReports"
                      name="natural.resources.use.reduction"
                    />,
                  ),
                },
              ]
            : [{ name: '...' }]
        }
      />
      <section className="content">
        <ModuleComsaSideBarReports
          activeRoute="user_module_comsaReports_natural_resources_use_reduction"
          moduleData={moduleData}
        />
        <div className="row">
          <div className="col-12">
            <Title>
              <Translator
                file="module-comsaReports"
                name="natural.resources.use.reduction"
              />
            </Title>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                {loading ? (
                  <div>
                    <Translator file="module-saicaReports" name="loading" />
                  </div>
                ) : (
                  <Formik
                    validateOnChange={false}
                    validateOnBlur={false}
                    enableReinitialize
                    initialValues={reportFormInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={editReport}
                  >
                    {({ setFieldValue, handleSubmit, values, errors }) => (
                      <>
                        <div className="border">
                          {' '}
                          <div className="p-3">
                            <div className="row">
                              <div className="col-md-6 offset-md-6 col-xl-3 offset-xl-9 text-center">
                                <div className="mb-3">
                                  <AppDatePicker
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="update.date"
                                      />
                                    }
                                    value={values.updateDate}
                                    onChange={(opt) =>
                                      setFieldValue('updateDate', opt)
                                    }
                                    minDate={handleMinDate(moduleData)}
                                    isClearable
                                  />
                                  {errors.updateDate && (
                                    <div className="text-danger">
                                      *{errors.updateDate}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-3 offset-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="total"
                                  />
                                </label>
                              </div>
                              <div className="col-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="direct"
                                  />
                                </label>
                              </div>
                              <div className="col-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="by.crushing"
                                  />
                                </label>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-3 text-center">
                                <h6>
                                  <Translator
                                    file="module-comsaReports"
                                    name="resource.reuse"
                                  />
                                  :
                                </h6>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.reuseResourcesTotal}
                                    onChange={(value) =>
                                      setFieldValue('reuseResourcesTotal', value)
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.reuseResourcesTotal && (
                                    <div className="text-danger">
                                      *{errors.reuseResourcesTotal}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.reuseResourcesDirect}
                                    onChange={(value) =>
                                      setFieldValue('reuseResourcesDirect', value)
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.reuseResourcesDirect && (
                                    <div className="text-danger">
                                      *{errors.reuseResourcesDirect}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.reuseResourcesCrushing}
                                    onChange={(value) =>
                                      setFieldValue('reuseResourcesCrushing', value)
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.reuseResourcesCrushing && (
                                    <div className="text-danger">
                                      *{errors.reuseResourcesCrushing}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-3 offset-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="project"
                                  />
                                </label>
                              </div>
                              <div className="col-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="real"
                                  />
                                </label>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12 col-md-3 text-center">
                                <h6>
                                  <Translator
                                    file="module-comsaReports"
                                    name="excavation.volume"
                                  />
                                  :
                                </h6>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.excavationVolumeProject}
                                    onChange={(value) =>
                                      setFieldValue('excavationVolumeProject', value)
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.excavationVolumeProject && (
                                    <div className="text-danger">
                                      *{errors.excavationVolumeProject}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.excavationVolumeReal}
                                    onChange={(value) =>
                                      setFieldValue('excavationVolumeReal', value)
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.excavationVolumeReal && (
                                    <div className="text-danger">
                                      *{errors.excavationVolumeReal}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-12 col-md-3 text-center">
                                <h6>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.disposal.soil.landfill.site"
                                  />
                                </h6>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={
                                      values.reductionLandDischargeLandfillProject
                                    }
                                    onChange={(value) =>
                                      setFieldValue(
                                        'reductionLandDischargeLandfillProject',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.reductionLandDischargeLandfillProject && (
                                    <div className="text-danger">
                                      *{errors.reductionLandDischargeLandfillProject}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.reductionLandDischargeLandfillReal}
                                    onChange={(value) =>
                                      setFieldValue(
                                        'reductionLandDischargeLandfillReal',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.reductionLandDischargeLandfillReal && (
                                    <div className="text-danger">
                                      *{errors.reductionLandDischargeLandfillReal}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-12 col-md-3 text-center">
                                <h6>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.loans.materials.quarry.project"
                                  />
                                </h6>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.loansUseProject}
                                    onChange={(value) =>
                                      setFieldValue('loansUseProject', value)
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.loansUseProject && (
                                    <div className="text-danger">
                                      *{errors.loansUseProject}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.loansUseReal}
                                    onChange={(value) =>
                                      setFieldValue('loansUseReal', value)
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.loansUseReal && (
                                    <div className="text-danger">
                                      *{errors.loansUseReal}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-12 col-md-3 text-center">
                                <h6>
                                  <Translator
                                    file="module-comsaReports"
                                    name="land.use.from.plants"
                                  />
                                  :
                                </h6>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.landUseFromPlantsProject}
                                    onChange={(value) =>
                                      setFieldValue('landUseFromPlantsProject', value)
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.landUseFromPlantsProject && (
                                    <div className="text-danger">
                                      *{errors.landUseFromPlantsProject}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-12 col-md-3">
                                <div className="mb-3">
                                  <div className="d-flex">
                                    {report.landUseFromPlantsEvidence && (
                                      <span
                                        title={ReactDOMServer.renderToString(
                                          <Translator
                                            file="module-comsaReports"
                                            name="download.evidence"
                                          />,
                                        )}
                                        className="btn btn-info mr-1"
                                        onClick={() =>
                                          getFile(
                                            'api_r_user_module_comsaReports_getLandUseFromPlantsEvidence',
                                            report.id,
                                            report.landUseFromPlantsEvidence.salt,
                                          )
                                        }
                                      >
                                        <AppRemixIcon icon="file-text" />
                                      </span>
                                    )}
                                    <AppFileUploader
                                      btnText={
                                        <Translator file="general" name="search..." />
                                      }
                                      placeholder={
                                        values.landUseFromPlantsEvidence
                                          ? ReactDOMServer.renderToString(
                                              <Translator
                                                file="general"
                                                name="one.file.selected"
                                              />,
                                            )
                                          : ReactDOMServer.renderToString(
                                              <Translator
                                                file="general"
                                                name="select.a.file"
                                              />,
                                            )
                                      }
                                      onChange={(opt) =>
                                        setFieldValue(
                                          'landUseFromPlantsEvidence',
                                          opt,
                                        )
                                      }
                                    />
                                  </div>
                                  {report.landUseFromPlantsEvidence && (
                                    <div>
                                      <label className="mb-0">
                                        <Translator
                                          file="module-comsaReports"
                                          name="delete"
                                        />
                                      </label>
                                      <label className="mt-1 pointer font-weight-light float-right">
                                        <input
                                          type="checkbox"
                                          className="mr-1"
                                          value={
                                            values.deleteLandUseFromPlantsEvidence
                                          }
                                          onChange={() =>
                                            setFieldValue(
                                              'deleteLandUseFromPlantsEvidence',
                                              !values.deleteLandUseFromPlantsEvidence,
                                            )
                                          }
                                          checked={
                                            values.deleteLandUseFromPlantsEvidence
                                          }
                                        />
                                        {report.landUseFromPlantsEvidence.name}
                                      </label>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12 col-md-3 text-center">
                                <h6>
                                  <Translator
                                    file="module-comsaReports"
                                    name="land.use.in.plants"
                                  />
                                  :
                                </h6>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.landUseInPlantsProject}
                                    onChange={(value) =>
                                      setFieldValue('landUseInPlantsProject', value)
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.landUseInPlantsProject && (
                                    <div className="text-danger">
                                      *{errors.landUseInPlantsProject}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-12 col-md-3">
                                <div className="mb-3">
                                  <div className="d-flex">
                                    {report.landUseInPlantsEvidence && (
                                      <span
                                        className="btn btn-info mr-1"
                                        title={ReactDOMServer.renderToString(
                                          <Translator
                                            file="module-comsaReports"
                                            name="download.evidence"
                                          />,
                                        )}
                                        onClick={() =>
                                          getFile(
                                            'api_r_user_module_comsaReports_getLandUseInPlantsEvidence',
                                            report.id,
                                            report.landUseInPlantsEvidence,
                                          )
                                        }
                                      >
                                        <AppRemixIcon icon="file-text" />
                                      </span>
                                    )}
                                    <AppFileUploader
                                      btnText={
                                        <Translator file="general" name="search..." />
                                      }
                                      placeholder={
                                        values.landUseInPlantsEvidence
                                          ? ReactDOMServer.renderToString(
                                              <Translator
                                                file="general"
                                                name="one.file.selected"
                                              />,
                                            )
                                          : ReactDOMServer.renderToString(
                                              <Translator
                                                file="general"
                                                name="select.a.file"
                                              />,
                                            )
                                      }
                                      onChange={(opt) =>
                                        setFieldValue('landUseInPlantsEvidence', opt)
                                      }
                                      id={1}
                                    />
                                  </div>
                                  {report.landUseInPlantsEvidence && (
                                    <div>
                                      <label className="mb-0">
                                        <Translator
                                          file="module-comsaReports"
                                          name="delete"
                                        />
                                      </label>
                                      <label className="mt-1 pointer font-weight-light float-right">
                                        <input
                                          type="checkbox"
                                          className="mr-1"
                                          value={values.deleteLandUseInPlantsEvidence}
                                          onChange={() =>
                                            setFieldValue(
                                              'deleteLandUseInPlantsEvidence',
                                              !values.deleteLandUseInPlantsEvidence,
                                            )
                                          }
                                          checked={
                                            values.deleteLandUseInPlantsEvidence
                                          }
                                        />
                                        {report.landUseInPlantsEvidence.name}
                                      </label>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="border mt-3">
                          <div className="p-3">
                            <div className="row">
                              <div className="col-md-3 offset-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.volume.wastewater.treated"
                                  />
                                </label>
                              </div>
                              <div className="col-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.estimated.water.usage"
                                  />
                                </label>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-12 col-md-3 text-center">
                                <h6>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.water"
                                  />
                                </h6>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.waterVolumeWasteWaterTreatedReal}
                                    onChange={(value) =>
                                      setFieldValue(
                                        'waterVolumeWasteWaterTreatedReal',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.waterVolumeWasteWaterTreatedReal && (
                                    <div className="text-danger">
                                      *{errors.waterVolumeWasteWaterTreatedReal}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.waterEstimatedWaterUsage}
                                    onChange={(value) =>
                                      setFieldValue('waterEstimatedWaterUsage', value)
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.waterEstimatedWaterUsage && (
                                    <div className="text-danger">
                                      *{errors.waterEstimatedWaterUsage}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-3 offset-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.use.recycled.water"
                                  />
                                </label>
                              </div>
                              <div className="col-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.estimated.water.use.fresh.water"
                                  />
                                </label>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12 col-md-3 text-center"></div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.waterActualUseRecycledWaterReal}
                                    onChange={(value) =>
                                      setFieldValue(
                                        'waterActualUseRecycledWaterReal',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.waterActualUseRecycledWaterReal && (
                                    <div className="text-danger">
                                      *{errors.waterActualUseRecycledWaterReal}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.waterUse}
                                    onChange={(value) =>
                                      setFieldValue('waterUse', value)
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.waterUse && (
                                    <div className="text-danger">
                                      *{errors.waterUse}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-3 offset-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.total.cost.works"
                                  />
                                </label>
                              </div>
                              <div className="col-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.waste.pgr"
                                  />
                                </label>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12 col-md-3 text-center">
                                <h6>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.waste"
                                  />
                                </h6>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.wasteTotalConstruction}
                                    onChange={(value) =>
                                      setFieldValue('wasteTotalConstruction', value)
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">kg</span>
                                      </div>
                                    }
                                  />
                                  {errors.wasteTotalConstruction && (
                                    <div className="text-danger">
                                      *{errors.wasteTotalConstruction}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.wasteAttorneyGeneralOffice}
                                    onChange={(value) =>
                                      setFieldValue(
                                        'wasteAttorneyGeneralOffice',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">kg</span>
                                      </div>
                                    }
                                  />
                                  {errors.wasteAttorneyGeneralOffice && (
                                    <div className="text-danger">
                                      *{errors.wasteAttorneyGeneralOffice}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-3 offset-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.reuse.soil.real.construction.site"
                                  />
                                </label>
                              </div>
                              <div className="col-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.reuse.soil.outside.real.construction.site"
                                  />
                                </label>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12 col-md-3 text-center">
                                <h6>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.land"
                                  />
                                </h6>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.landsReuseSoilConstructionReal}
                                    onChange={(value) =>
                                      setFieldValue(
                                        'landsReuseSoilConstructionReal',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.landsReuseSoilConstructionReal && (
                                    <div className="text-danger">
                                      *{errors.landsReuseSoilConstructionReal}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={
                                      values.landsReuseSoilOutsideConstructionReal
                                    }
                                    onChange={(value) =>
                                      setFieldValue(
                                        'landsReuseSoilOutsideConstructionReal',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.landsReuseSoilOutsideConstructionReal && (
                                    <div className="text-danger">
                                      *{errors.landsReuseSoilOutsideConstructionReal}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-3 offset-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.estimated.land.reuse.project"
                                  />
                                </label>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12 col-md-3 text-center"></div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.landsEstimatedLandReuseProject}
                                    onChange={(value) =>
                                      setFieldValue(
                                        'landsEstimatedLandReuseProject',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.landsEstimatedLandReuseProject && (
                                    <div className="text-danger">
                                      *{errors.landsEstimatedLandReuseProject}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-3 offset-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.recycling.stone.materials.off.site"
                                  />
                                </label>
                              </div>
                              <div className="col-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.on.site.recycling.stone.materials"
                                  />{' '}
                                </label>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12 col-md-3 text-center">
                                <h6>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.rock.material"
                                  />
                                </h6>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.stoneRecyclingStoneMaterialsOffsite}
                                    onChange={(value) =>
                                      setFieldValue(
                                        'stoneRecyclingStoneMaterialsOffsite',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.stoneRecyclingStoneMaterialsOffsite && (
                                    <div className="text-danger">
                                      *{errors.stoneRecyclingStoneMaterialsOffsite}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.stoneRecyclingStoneMaterialsOnSite}
                                    onChange={(value) =>
                                      setFieldValue(
                                        'stoneRecyclingStoneMaterialsOnSite',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.stoneRecyclingStoneMaterialsOnSite && (
                                    <div className="text-danger">
                                      *{errors.stoneRecyclingStoneMaterialsOnSite}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-3 offset-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.estimated.rock.material.project"
                                  />
                                </label>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12 col-md-3 text-center" />
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.stoneEstimatedRockMaterialProject}
                                    onChange={(value) =>
                                      setFieldValue(
                                        'stoneEstimatedRockMaterialProject',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.stoneEstimatedRockMaterialProject && (
                                    <div className="text-danger">
                                      *{errors.stoneEstimatedRockMaterialProject}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-3 offset-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.genuine.recycled.materials"
                                  />
                                </label>
                              </div>
                              <div className="col-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.use.recycled.materials.proyecto"
                                  />
                                </label>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-12 col-md-3 text-center">
                                <h6>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.use.reused.recycled.materials"
                                  />
                                </h6>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.consumptionRecycledMaterialsReal}
                                    onChange={(value) =>
                                      setFieldValue(
                                        'consumptionRecycledMaterialsReal',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.consumptionRecycledMaterialsReal && (
                                    <div className="text-danger">
                                      *{errors.consumptionRecycledMaterialsReal}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={
                                      values.consumptionUseRecycledMaterialsProject
                                    }
                                    onChange={(value) =>
                                      setFieldValue(
                                        'consumptionUseRecycledMaterialsProject',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.consumptionUseRecycledMaterialsProject && (
                                    <div className="text-danger">
                                      *{errors.consumptionUseRecycledMaterialsProject}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-3 offset-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.soil.other.construction.sites"
                                  />
                                </label>
                              </div>
                              <div className="col-md-3 text-center">
                                <label>
                                  <Translator
                                    file="module-comsaReports"
                                    name="module-comsaReports.use.soil.other.construction.sites"
                                  />
                                </label>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12 col-md-3 text-center"></div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.consumptionSoilOtherConstruction}
                                    onChange={(value) =>
                                      setFieldValue(
                                        'consumptionSoilOtherConstruction',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.consumptionSoilOtherConstruction && (
                                    <div className="text-danger">
                                      *{errors.consumptionSoilOtherConstruction}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div className="mb-3">
                                  <AppInputNumber
                                    value={values.consumptionFromOtherConstruction}
                                    onChange={(value) =>
                                      setFieldValue(
                                        'consumptionFromOtherConstruction',
                                        value,
                                      )
                                    }
                                    icon={<AppRemixIcon icon="hashtag" noVariants />}
                                    isClearable
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">m3</span>
                                      </div>
                                    }
                                  />
                                  {errors.consumptionFromOtherConstruction && (
                                    <div className="text-danger">
                                      *{errors.consumptionFromOtherConstruction}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="row mt-3">
                          <div className="col-12">
                            <AppTextArea
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-comsaReports"
                                  name="m-cr-comment"
                                />,
                              )}
                              value={values?.comment}
                              onChange={(v) => setFieldValue(`comment`, v)}
                              height="89px"
                            />
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-12">
                            <button
                              className="btn btn-success float-right"
                              type="submit"
                              onClick={handleSubmit}
                              disabled={isSendingReport}
                            >
                              <Translator file="module-comsaReports" name="edit" />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </Formik>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ModuleComsaNaturalResourcesUseReductionReportScreen;

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;
